import { t } from "@lingui/macro";

export enum SquarePaymentErrorCode {
  AddressVerificationFailure = "ADDRESS_VERIFICATION_FAILURE",
  CardExpired = "CARD_EXPIRED",
  CardNotSupported = "CARD_NOT_SUPPORTED",
  CardTokenExpired = "CARD_TOKEN_EXPIRED",
  CardTokenUsed = "CARD_TOKEN_USED",
  CvvFailure = "CVV_FAILURE",
  ExpirationFailure = "EXPIRATION_FAILURE",
  GenericDecline = "GENERIC_DECLINE",
  InsufficientFunds = "INSUFFICIENT_FUNDS",
  InvalidAccount = "INVALID_ACCOUNT",
  InvalidCard = "INVALID_CARD",
  InvalidCardData = "INVALID_CARD_DATA",
  InvalidExpiration = "INVALID_EXPIRATION",
  InvalidExpirationDate = "INVALID_EXPIRATION_DATE",
  InvalidExpirationYear = "INVALID_EXPIRATION_YEAR",
  PanFailure = "PAN_FAILURE",
  PaymentLimitExceeded = "PAYMENT_LIMIT_EXCEEDED",
  TransactionLimit = "TRANSACTION_LIMIT",
  UnsupportedCardBrand = "UNSUPPORTED_CARD_BRAND",
  VoiceFailure = "VOICE_FAILURE",
}

const SQUARE_ERROR_CODE_PATTERN =
  /^(?:Error:\s*|Authorization error:\s*')([A-Z0-9_]+)'?$/;
const GRAPHQL_ERROR_CODE_PATTERN = /(?:^|,)_?ERROR:_?([A-Z0-9_]+)$/;

export const normalizeSquarePaymentErrorCode = (
  code?: string | null
): string | null => {
  if (!code) {
    return null;
  }

  const trimmedCode = code.trim();
  return (
    trimmedCode.match(SQUARE_ERROR_CODE_PATTERN)?.[1] ??
    trimmedCode.match(GRAPHQL_ERROR_CODE_PATTERN)?.[1] ??
    trimmedCode
  );
};

export const getSquarePaymentErrorMessage = (
  code?: string | null
): string | null => {
  switch (normalizeSquarePaymentErrorCode(code)) {
    case SquarePaymentErrorCode.AddressVerificationFailure:
      return t`The card was declined because the billing address could not be verified. Please check your billing information or try another card.`;
    case SquarePaymentErrorCode.CardExpired:
      return t`This card is expired. Please use a different card.`;
    case SquarePaymentErrorCode.CardNotSupported:
      return t`This card type is not supported. Please use a different card.`;
    case SquarePaymentErrorCode.CardTokenExpired:
      return t`This payment method has expired. Please try again or use a different card.`;
    case SquarePaymentErrorCode.CardTokenUsed:
      return t`This payment could not be completed. Please try again.`;
    case SquarePaymentErrorCode.CvvFailure:
      return t`The card security code is incorrect. Please check it or use a different card.`;
    case SquarePaymentErrorCode.ExpirationFailure:
      return t`This card is expired or has an invalid expiration date. Please use a different card.`;
    case SquarePaymentErrorCode.GenericDecline:
      return t`The card was declined. Please contact your bank or use a different card.`;
    case SquarePaymentErrorCode.InsufficientFunds:
      return t`The card was declined due to insufficient funds. Please use a different card.`;
    case SquarePaymentErrorCode.InvalidAccount:
      return t`The card account could not be found. Please use a different card.`;
    case SquarePaymentErrorCode.InvalidCard:
      return t`The card details could not be verified. Please check them or use a different card.`;
    case SquarePaymentErrorCode.InvalidCardData:
      return t`The card details are invalid. Please check them or use a different card.`;
    case SquarePaymentErrorCode.InvalidExpiration:
    case SquarePaymentErrorCode.InvalidExpirationDate:
      return t`The card expiration date is invalid. Please check it or use a different card.`;
    case SquarePaymentErrorCode.InvalidExpirationYear:
      return t`The card expiration year is invalid. Please check it or use a different card.`;
    case SquarePaymentErrorCode.PanFailure:
      return t`The card number is invalid. Please check it or use a different card.`;
    case SquarePaymentErrorCode.PaymentLimitExceeded:
      return t`This payment exceeds the payment limit for the club. Please use a different card or contact the club.`;
    case SquarePaymentErrorCode.TransactionLimit:
      return t`This payment exceeds the card's transaction limit. Please use a different card or contact your bank.`;
    case SquarePaymentErrorCode.UnsupportedCardBrand:
      return t`This card brand is not supported. Please use a different card.`;
    case SquarePaymentErrorCode.VoiceFailure:
      return t`Your bank needs to approve this payment. Please contact your bank or use a different card.`;
    default:
      return null;
  }
};
